/**
 * /pipeline-facility/invoices/:id — one PFI- invoice on its own page
 * (fieldiq-api#1229, PF-5a). Opened from the register, Ready to invoice, a
 * job's Invoices panel or a ticket's "On invoice" link. The back arrow returns
 * to the register with its filters.
 *
 * EVERY FIGURE IS THE API'S, AS STORED AT GENERATION — lines, subtotal, each
 * levied tax, the tax's provenance and the total come from
 * GET /invoices/:id, the loader the PDF and the send also use. Nothing here
 * adds lines up or computes tax.
 *
 * ACTIONS (band, top right; admin and office staff — everyone else reads):
 *   draft              PDF · Send · Void
 *   sent / approved    PDF · Send again · Mark paid · Void
 *   paid / void        PDF. Final and read-only.
 * Each opens INLINE under the band (InvoiceActions.tsx), no modal. When the
 * company default priced the tax (`taxRateSource` company_columns), the page
 * says why; a fallback the send refuses (`tax_fallback_blocked`) holds Send
 * and says what to do instead.
 */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { AlertTriangle, Ban, CheckCircle2, FileText, Send } from 'lucide-react';
import { api, getBlobApiErrorMessage } from '../../../api/client';
import { PageHeaderBand } from '../../../components/layout/PageHeaderBand';
import { LoadFailureNotice } from '../../../components/ui/LoadFailureNotice';
import { useAuth } from '../../../contexts/AuthContext';
import { useTenantTimeZone } from '../../../hooks/useTenantTimeZone';
import { usePfInvoice } from '../../../hooks/usePfInvoices';
import { claimBlobTab, POPUP_BLOCKED_MESSAGE, showBlobInTab } from '../../../lib/blob-tab';
import { formatInstant } from '../../../lib/calendar-date';
import { fmtCurrency } from '../../../lib/display-labels';
import { queryFailureError, queryLoadFailure } from '../../../lib/load-failure';
import { fmtNumber, pfDay } from '../../../lib/pipeline-facility';
import { pfRateUnitSuffix, pfScopeText } from '../../../lib/pf-tickets';
import {
  canWritePfInvoices,
  pfInvoiceCanMarkPaid,
  pfInvoiceIsFinal,
  pfInvoiceIsLive,
  pfInvoiceIsTaxFallback,
  pfInvoiceLineGroups,
  pfInvoiceSendBlocked,
  pfInvoiceStatusSkin,
  pfLeviedTaxRows,
  pfReleasedTicketNumbers,
  pfTaxFallbackText,
  pfTaxRowLabel,
  pfTaxSourceText,
} from '../../../lib/pf-invoices';
import type { PfInvoice } from '../../../types/pf-invoices';
import { DH_BAND_SECONDARY } from '../../downhole/DownholeBits';
import { useBackToList } from '../../downhole/recordPage';
import { pfRateText } from '../tickets/TicketBits';
import { isNotFound, PF_EYEBROW, PF_PRIMARY, PfBadge, PfEmpty, PfFormError, PfLoading, PfPanel, PfSpec } from '../PfBits';
import { PfInvoiceMarkPaidPanel, PfInvoiceSendPanel, PfInvoiceVoidPanel, PfReleasedNotice } from './InvoiceActions';

const LIST = '/pipeline-facility/invoices';
const at = (v: string | null | undefined, tz: string | null) => (v ? formatInstant(v, tz) ?? '—' : '—');

/** A line's quantity: `10.5 hr`, `1 day`, `42 km`, `3 each`. Absent → `—`. */
const qtyText = (q: number | null, unit: string, distanceUnit: string | null) => {
  const v = fmtNumber(q);
  if (v === '—') return v;
  const u = pfRateUnitSuffix(unit, distanceUnit).replace(/^\//, '').trim();
  return u ? `${v} ${u}` : v;
};

/** Said when the company default priced the tax: amber when the send refuses it, neutral when it is benign. */
export function PfTaxFallbackNotice({ inv }: { inv: PfInvoice }) {
  const text = pfTaxFallbackText(inv);
  if (!text) return null;
  const blocked = pfInvoiceSendBlocked(inv);
  return (
    <div
      role="note"
      data-testid="pf-invoice-tax-fallback"
      data-blocked={blocked ? 'true' : 'false'}
      className={`flex items-start gap-3 rounded-fi-panel border border-fieldiq-border border-l-4 bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text ${blocked ? 'border-l-fi-amber' : 'border-l-fi-steel'}`}
    >
      {blocked && <AlertTriangle className="mt-0.5 size-4 shrink-0 text-fi-signal-amber" aria-hidden="true" />}
      <div>
        <p className="font-bold">{blocked ? 'This invoice cannot be sent.' : 'Tax from the company settings.'}</p>
        <p className="mt-1">{text}</p>
      </div>
    </div>
  );
}

export default function PfInvoiceDetailScreen() {
  const { id } = useParams<{ id: string }>();
  const back = useBackToList(LIST);
  const { user, company } = useAuth();
  const showsPst = company?.country === 'CA';
  const tz = useTenantTimeZone();
  const query = usePfInvoice(id);
  const inv = query.data?.data ?? null;
  const failure = queryLoadFailure(query);
  const notFound = !inv && !query.isLoading && (isNotFound(queryFailureError(query)) || !failure);
  const [panel, setPanel] = useState<'send' | 'paid' | 'void' | null>(null);
  const [released, setReleased] = useState<string[] | null>(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);

  const canWrite = canWritePfInvoices(user?.role);
  const status = inv?.status ?? null;
  const live = pfInvoiceIsLive(status);
  const sendBlocked = !!inv && pfInvoiceSendBlocked(inv);
  const cur = inv?.currency ?? null;

  const openPdf = async () => {
    if (!inv) return;
    setPdfError(null);
    const win = claimBlobTab();
    if (!win) return setPdfError(POPUP_BLOCKED_MESSAGE);
    setPdfBusy(true);
    try {
      const res = await api.pipelineFacility.invoices.pdf(inv.id);
      showBlobInTab(win, res.data);
    } catch (e) {
      win.close();
      setPdfError(await getBlobApiErrorMessage(e, 'The PDF could not be made.'));
    } finally {
      setPdfBusy(false);
    }
  };

  const actions = inv ? (
    <div className="flex flex-wrap gap-2" data-testid="pf-invoice-actions">
      <button type="button" className={DH_BAND_SECONDARY} onClick={openPdf} disabled={pdfBusy} data-testid="pf-invoice-pdf">
        <FileText className="size-4" aria-hidden="true" />
        {pdfBusy ? 'Opening…' : 'PDF'}
      </button>
      {canWrite && live && (
        <button type="button" className={DH_BAND_SECONDARY} onClick={() => setPanel('void')} data-testid="pf-invoice-void">
          <Ban className="size-4" aria-hidden="true" />
          Void
        </button>
      )}
      {canWrite && pfInvoiceCanMarkPaid(status) && (
        <button type="button" className={DH_BAND_SECONDARY} onClick={() => setPanel('paid')} data-testid="pf-invoice-mark-paid">
          <CheckCircle2 className="size-4" aria-hidden="true" />
          Mark paid
        </button>
      )}
      {canWrite && live && !sendBlocked && (
        <button type="button" className={PF_PRIMARY} onClick={() => setPanel('send')} data-testid="pf-invoice-send">
          <Send className="size-4" aria-hidden="true" />
          {inv.sentAt ? 'Send again' : 'Send'}
        </button>
      )}
    </div>
  ) : undefined;

  return (
    <div data-testid="pf-invoice-page" className="animate-fade-in min-h-full bg-fieldiq-dark">
      <PageHeaderBand
        eyebrow={PF_EYEBROW}
        title={inv?.invoiceNumber || (query.isLoading ? 'Invoice' : '—')}
        description={inv ? `${inv.customerName ?? '—'} · ${inv.jobNumber ?? (inv.jobId ? '—' : 'Several jobs')}` : undefined}
        onBack={back}
        backLabel="Back to invoices"
        actions={actions}
      />
      <section className="px-[var(--fi-page-inline)] py-[var(--fi-page-block)] pb-[var(--bottom-chrome)]">
        <div className="mx-auto max-w-[1920px] space-y-4" data-testid="pf-invoice-detail">
          {!inv && query.isLoading ? (
            <PfLoading label="Loading the invoice" />
          ) : notFound ? (
            <PfPanel eyebrow="Invoice" title="Invoice">
              <PfEmpty>This invoice was not found.</PfEmpty>
            </PfPanel>
          ) : !inv ? (
            <PfPanel eyebrow="Invoice" title="Invoice">
              <LoadFailureNotice error={queryFailureError(query)} kind={failure ?? 'unreachable'} subject="this invoice" />
            </PfPanel>
          ) : (
            <>
              <PfFormError message={pdfError} />
              {released && <PfReleasedNotice numbers={pfReleasedTicketNumbers(inv, released)} onDismiss={() => setReleased(null)} />}
              {status === 'void' && (
                <div role="status" data-testid="pf-invoice-void-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-red bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  <p className="font-bold">Void {at(inv.voidedAt, tz)}{inv.voidedByName ? ` by ${inv.voidedByName}` : ''}. This invoice is final and read-only.</p>
                  <p className="mt-1 whitespace-pre-wrap">{inv.voidReason || '—'}</p>
                </div>
              )}
              {status === 'paid' && (
                <p role="status" data-testid="pf-invoice-paid-note" className="rounded-fi-panel border border-fieldiq-border border-l-4 border-l-fi-green bg-fieldiq-card px-4 py-3 text-sm text-fieldiq-text">
                  Paid {at(inv.paidAt, tz)}{inv.paidByName ? `, recorded by ${inv.paidByName}` : ''}. This invoice is final and read-only.
                </p>
              )}
              {!pfInvoiceIsFinal(status) && pfInvoiceIsTaxFallback(inv) && <PfTaxFallbackNotice inv={inv} />}
              {canWrite && live && panel === 'send' && !sendBlocked && <PfInvoiceSendPanel inv={inv} tz={tz} onDone={() => setPanel(null)} />}
              {canWrite && pfInvoiceCanMarkPaid(status) && panel === 'paid' && <PfInvoiceMarkPaidPanel inv={inv} onDone={() => setPanel(null)} />}
              {canWrite && live && panel === 'void' && <PfInvoiceVoidPanel inv={inv} onDone={() => setPanel(null)} onVoided={setReleased} />}

              <PfPanel eyebrow="Invoice" title="Details" testId="pf-invoice-details">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <PfSpec label="Status" value={<span data-testid="pf-invoice-status"><PfBadge skin={pfInvoiceStatusSkin(status)} value={status} /></span>} />
                  <PfSpec label="Invoice date" value={pfDay(inv.invoiceDate)} />
                  <PfSpec label="Due date" value={pfDay(inv.dueDate)} />
                  <PfSpec label="Currency" value={inv.currency || '—'} />
                  <PfSpec label="Job" value={inv.jobId ? <Link className="underline" to={`/pipeline-facility/jobs/${inv.jobId}?tab=tickets`}>{inv.jobNumber || '—'}</Link> : 'Several jobs'} mono />
                  <PfSpec label="Site" value={inv.siteName || '—'} />
                  <PfSpec label="Province / state" value={inv.jurisdiction || '—'} />
                  <PfSpec label="LSD / legal location" value={inv.lsd || '—'} mono />
                  <PfSpec label="PO number" value={inv.poNumber || '—'} mono />
                  <PfSpec label="AFE number" value={inv.afeNumber || '—'} mono />
                  <PfSpec label="Cost code" value={inv.costCode || '—'} mono />
                  <PfSpec label="Created" value={`${at(inv.createdAt, tz)}${inv.createdByName ? ` by ${inv.createdByName}` : ''}`} />
                  <PfSpec label="Last sent" value={inv.sentAt ? `${at(inv.sentAt, tz)} to ${inv.sentTo || '—'}` : 'Not sent'} />
                  {inv.sentCc && inv.sentCc.length > 0 && <PfSpec label="Copied to" value={inv.sentCc.join(', ')} />}
                  {showsPst && <PfSpec label="PST exemption" value={inv.pstExempt ? inv.pstExemptNumber || 'Exempt' : '—'} />}
                </dl>
                {inv.notes && <p className="whitespace-pre-wrap border-t border-fieldiq-border px-4 py-3 text-sm text-fieldiq-text">{inv.notes}</p>}
              </PfPanel>

              <PfPanel eyebrow="Bill to" title={inv.customerName || '—'} testId="pf-invoice-customer">
                <dl className="grid gap-4 px-4 py-4 sm:grid-cols-2 lg:grid-cols-4">
                  <PfSpec label="Billing address" value={inv.customerAddress || '—'} />
                  <PfSpec label="Main contact" value={inv.mainContactName || '—'} />
                  <PfSpec label="Main contact email" value={inv.mainContactEmail || '—'} />
                  <PfSpec label="Accounting email" value={inv.accountingEmail || '—'} />
                </dl>
              </PfPanel>

              <PfPanel eyebrow="Billed" title="Field tickets" testId="pf-invoice-tickets">
                {inv.tickets.length === 0 ? (
                  <PfEmpty>This invoice lists no field tickets.</PfEmpty>
                ) : (
                  <ul className="divide-y divide-fieldiq-border">
                    {inv.tickets.map((t) => (
                      <li key={t.id} className="flex flex-wrap items-center gap-x-4 gap-y-1 px-4 py-3 text-sm" data-testid="pf-invoice-ticket">
                        <Link to={`/pipeline-facility/tickets/${t.id}`} className="font-mono font-extrabold text-white underline underline-offset-2">
                          {t.ticketNumber}
                        </Link>
                        <span className="font-mono text-fieldiq-text">{t.jobNumber || '—'}</span>
                        <span className="text-fieldiq-text">{pfScopeText({ scope: t.scope, workDate: t.workDate })}</span>
                        <span className="text-fieldiq-muted">Approved by {t.customerApprovedByName || '—'}</span>
                        <span className="font-mono text-fieldiq-muted">PO {t.poNumber || '—'}</span>
                        <span className="font-mono text-fieldiq-text">{fmtCurrency(t.subtotal, cur)} before tax</span>
                        {t.releasedAt && <span className="text-fieldiq-muted">Released {at(t.releasedAt, tz)}</span>}
                      </li>
                    ))}
                  </ul>
                )}
              </PfPanel>

              <PfPanel eyebrow="Charges" title="Lines" testId="pf-invoice-lines">
                {inv.lines.length === 0 ? (
                  <PfEmpty>This invoice has no lines.</PfEmpty>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full min-w-[680px] text-left">
                      <thead className="border-b border-fieldiq-border bg-fieldiq-dark/40 text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">
                        <tr className="border-l-4 border-l-transparent">
                          <th scope="col" className="px-4 py-3">Charge</th>
                          <th scope="col" className="px-4 py-3 text-right">Qty</th>
                          <th scope="col" className="px-4 py-3 text-right">Rate</th>
                          <th scope="col" className="px-4 py-3 text-right">Amount</th>
                        </tr>
                      </thead>
                      {pfInvoiceLineGroups(inv).map((g) => {
                        const t = inv.tickets.find((x) => x.id === g.ticketId);
                        return (
                          <tbody key={g.ticketId} data-testid="pf-invoice-line-group">
                            <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent bg-fieldiq-dark/30">
                              <th scope="colgroup" colSpan={4} className="px-4 py-2 text-left text-xs font-bold text-fieldiq-text">
                                <span className="font-mono">{g.ticketNumber}</span>
                                {t ? ` · ${pfScopeText({ scope: t.scope, workDate: t.workDate })}` : ''}
                                {` · Approved by ${t?.customerApprovedByName || '—'}`}
                              </th>
                            </tr>
                            {g.lines.map((l) => (
                              <tr key={l.id} data-testid="pf-invoice-line" className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                                <td className="px-4 py-3">
                                  <p className="text-sm font-semibold text-white">{l.description || '—'}</p>
                                  <p className="mt-0.5 text-xs text-fieldiq-muted">
                                    {[l.rateCode, l.kindLabel, l.workDate ? pfDay(l.workDate) : null, l.standby ? 'Standby' : null].filter(Boolean).join(' · ') || '—'}
                                  </p>
                                </td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{qtyText(l.quantity, l.unit, l.distanceUnit)}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-fieldiq-text">{pfRateText(l.rate, cur, l.unit, l.distanceUnit)}</td>
                                <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(l.amount, cur)}</td>
                              </tr>
                            ))}
                          </tbody>
                        );
                      })}
                      <tfoot>
                        <tr className="border-t-2 border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-2 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Subtotal</th>
                          <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white" data-testid="pf-invoice-subtotal">{fmtCurrency(inv.subtotal, cur)}</td>
                        </tr>
                        {(() => {
                          const rows = pfLeviedTaxRows(inv);
                          return rows.length > 0 ? (
                            rows.map((r, i) => (
                              <tr key={i} className="border-l-4 border-l-transparent" data-testid="pf-invoice-tax-row">
                                <th scope="row" colSpan={3} className="px-4 py-2 text-right text-sm font-semibold text-fieldiq-text">{pfTaxRowLabel(r.label, r.percent)}</th>
                                <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(r.amount, cur)}</td>
                              </tr>
                            ))
                          ) : (
                            <tr className="border-l-4 border-l-transparent" data-testid="pf-invoice-tax-row">
                              <th scope="row" colSpan={3} className="px-4 py-2 text-right text-sm font-semibold text-fieldiq-text">Tax</th>
                              <td className="whitespace-nowrap px-4 py-2 text-right font-mono text-sm tabular-nums text-white">{fmtCurrency(inv.taxAmount, cur)}</td>
                            </tr>
                          );
                        })()}
                        <tr className="border-l-4 border-l-transparent">
                          <td colSpan={4} className="px-4 pb-2 text-right text-xs text-fieldiq-muted" data-testid="pf-invoice-tax-source">
                            Tax: {pfTaxSourceText(inv)}
                          </td>
                        </tr>
                        <tr className="border-t border-l-4 border-fieldiq-border border-l-transparent">
                          <th scope="row" colSpan={3} className="px-4 py-3 text-right text-[0.64rem] font-black uppercase tracking-[0.12em] text-fieldiq-muted">Total ({inv.currency || '—'})</th>
                          <td className="whitespace-nowrap px-4 py-3 text-right font-mono text-base font-extrabold tabular-nums text-white" data-testid="pf-invoice-total">{fmtCurrency(inv.total, cur)}</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                )}
              </PfPanel>
            </>
          )}
        </div>
      </section>
    </div>
  );
}
